import { lookup } from "@repo/vihangam-yog-shared";

/**
 * Plain words for a failed save or load. Raw server and network text
 * ("Failed to fetch", "That tier seats up to 8", a validation dump) never
 * reaches a volunteer: every error shown in the app goes through here.
 */

/** What a volunteer should do when the request never reached us. */
// fallow-ignore-next-line unused-export -- exercised directly by its unit test
export const NETWORK_ERROR_SENTENCE =
  "That didn't save. Check your internet connection and try again.";

/** Anything we can't explain more precisely. */
export const GENERIC_ERROR_SENTENCE =
  "Something went wrong, so that didn't work. Try again in a moment.";

/** Browser wording for "the request never got an answer": Chrome,
 *  Safari, Firefox, React Native and fetch timeouts. */
const NETWORK_MESSAGE =
  /failed to fetch|load failed|networkerror|network request failed|network connection was lost|internet connection appears to be offline|fetch failed|timed? ?out|aborted/i;

const NETWORK_ERROR_NAMES = new Set([
  "TypeError",
  "AbortError",
  "TimeoutError",
]);

/** tRPC codes whose meaning doesn't depend on the message. */
const CODE_SENTENCE = {
  UNAUTHORIZED:
    "You've been signed out. Reload the page, sign in again and try once more.",
  FORBIDDEN:
    "Your access doesn't allow that. Ask an organiser if you need to do it.",
  TOO_MANY_REQUESTS:
    "That was tried too many times in a row. Wait a minute and try again.",
} satisfies Record<string, string>;

/** Codes whose message is a sentence written for people (a guest who is
 *  already gone, a type of place that is full), not a system failure. */
const PEOPLE_FACING_CODES = new Set([
  "BAD_REQUEST",
  "CONFLICT",
  "NOT_FOUND",
  "PRECONDITION_FAILED",
  "PAYLOAD_TOO_LARGE",
]);

/** Backend sentences the team app is known to receive, in plain words. */
const KNOWN_SENTENCES: readonly [RegExp, (match: RegExpExecArray) => string][] =
  [
    [
      /^That tier seats up to (\d+)\. Choose a larger tier for (?:your|this) group\.$/,
      (m) =>
        m[1] === "1"
          ? "That type of place is for 1 person. Choose a bigger type of place for this group."
          : `That type of place seats up to ${m[1]} people. Choose a bigger type of place for this group.`,
    ],
    [
      /^(.+) is full: (\d+) of (\d+) booked\.$/,
      (m) => `${m[1]} is full. All ${m[3]} places are booked.`,
    ],
    [
      /^That registration no longer exists\.$/,
      () =>
        "That booking no longer exists. Reload the page to see the latest list.",
    ],
    [
      /^This registration has no email on file\.$/,
      () => "This booking has no email address, so nothing was sent.",
    ],
    [
      /^No one is listed at that position on this registration\.$/,
      () =>
        "That person is no longer on this booking. Reload the page and try again.",
    ],
    [
      /^That inquiry no longer exists\.$/,
      () => "That website message no longer exists. Reload the page.",
    ],
    [
      /^That user no longer exists in Clerk\.$/,
      () => "That person's account no longer exists.",
    ],
    [
      /^(?:Unknown event: .*|That event is no longer in the catalog\.)$/,
      () => "That event isn't in the list any more. Reload the page.",
    ],
    [
      /^(?:A test email address is required(?: for a dry run)?\.|At least one test email address is required\.)$/,
      () => "Enter an email address for the test first.",
    ],
    [
      /^POSTMARK_SERVER_TOKEN is not set/,
      () => "Email sending isn't switched on yet, so nothing was sent.",
    ],
    [
      /^Postmark send failed\.$/,
      () => "The email didn't go out. Try again in a moment.",
    ],
    [
      /^Guest broadcasts are set up for the Australian chapter\.$/,
      () => "Emails to guests can only be sent from the Australian team app.",
    ],
  ];

/** Words in a server message that mean it was written for developers. */
const TECHNICAL_WORDS =
  /\b(?:clerk|postmark|stripe|d1|sql|sqlite|json|zod|trpc|undefined|null|nan|token|env|environment|wrangler|api|http|status|slug|catalog|credentials?|configured|schema|invalid_\w+)\b/i;

/** A short, finished sentence with no code-like punctuation. */
const PLAIN_SENTENCE = /^[A-Z][^{}[\]<>_=`|\\]{0,158}[.!?]$/;

/** The admin's words for backend nouns (see the glossary in the sweep spec). */
const WORD_SWAPS: readonly [RegExp, string][] = [
  [/\bTiers\b/g, "Types of place"],
  [/\btiers\b/g, "types of place"],
  [/\bTier\b/g, "Type of place"],
  [/\btier\b/g, "type of place"],
  [/\bRegistrations\b/g, "Bookings"],
  [/\bregistrations\b/g, "bookings"],
  [/\bRegistration\b/g, "Booking"],
  [/\bregistration\b/g, "booking"],
  [/\binquiry\b/g, "website message"],
  [/\binquiries\b/g, "website messages"],
];

/** The error's tRPC code ("BAD_REQUEST"), when it carries one. */
function trpcCode(err: Error): string | null {
  if (!("data" in err) || !(err.data instanceof Object)) {
    return null;
  }
  return "code" in err.data ? String(err.data.code) : null;
}

function isNetworkFailure(err: Error): boolean {
  if (NETWORK_ERROR_NAMES.has(err.name) || NETWORK_MESSAGE.test(err.message)) {
    return true;
  }
  // tRPC wraps the browser's TypeError as the client error's cause.
  return err.cause instanceof Error && err.cause !== err
    ? isNetworkFailure(err.cause)
    : false;
}

function swapWords(sentence: string): string {
  return WORD_SWAPS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    sentence,
  );
}

/**
 * The sentence to show for a failed request. `whenOffline` replaces the
 * network sentence where a page has its own wording (Reception: "Check
 * your signal and tap Check in again.").
 */
export function friendlyError(
  // oxlint-disable-next-line anti-slop/no-unknown-parameters -- caught errors: onError callbacks and catch blocks hand over whatever was thrown
  err: unknown,
  whenOffline: string = NETWORK_ERROR_SENTENCE,
): string {
  if (!(err instanceof Error)) {
    return GENERIC_ERROR_SENTENCE;
  }
  if (isNetworkFailure(err)) {
    return whenOffline;
  }
  const message = err.message.trim();
  for (const [pattern, rewrite] of KNOWN_SENTENCES) {
    const match = pattern.exec(message);
    if (match) {
      return rewrite(match);
    }
  }
  const code = trpcCode(err);
  if (code === "TIMEOUT") {
    return whenOffline;
  }
  const byCode = code === null ? undefined : lookup(CODE_SENTENCE, code);
  if (byCode) {
    return byCode;
  }
  if (
    code !== null &&
    PEOPLE_FACING_CODES.has(code) &&
    PLAIN_SENTENCE.test(message) &&
    !TECHNICAL_WORDS.test(message)
  ) {
    return swapWords(message);
  }
  return GENERIC_ERROR_SENTENCE;
}
